import { useEffect, useRef, useState } from 'react';
import { EmptyState, Text } from '../../ui';
import { useMapProvider } from './MapProviderContext';
import type { MapController, MapView } from './MapProvider';

/** Props for {@link LocationPickerMap}. */
export interface LocationPickerMapProps {
  /** The currently chosen point, when one has been picked. */
  readonly value: { readonly latitude: number; readonly longitude: number } | null;
  /** Where the map starts when nothing is chosen yet. */
  readonly initialView: MapView;
  /** Called with the coordinates the user taps. */
  readonly onPick: (latitude: number, longitude: number) => void;
  /** Height of the map surface. */
  readonly height?: number;
}

/**
 * A map for choosing a venue: tapping drops a pin and reports its coordinates.
 *
 * Falls back to the same map-unavailable state as {@link EventsMap}; when the map cannot load the
 * form still accepts a location typed into the latitude and longitude fields beside it.
 *
 * @param props - Component props.
 * @returns The picker element.
 */
export function LocationPickerMap({
  value,
  initialView,
  onPick,
  height = 320,
}: LocationPickerMapProps): React.JSX.Element {
  const provider = useMapProvider();
  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<MapController | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;

    if (container === null) {
      return;
    }

    void (async () => {
      try {
        const controller = await provider.create(container, {
          view: initialView,
          onMapClick: (latitude, longitude) => onPickRef.current(latitude, longitude),
        });

        if (cancelled) {
          controller?.destroy();
          return;
        }

        if (controller === null) {
          setUnavailable(true);
          return;
        }

        controllerRef.current = controller;
      } catch {
        if (!cancelled) {
          setUnavailable(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [provider]);

  useEffect(() => {
    if (value !== null) {
      controllerRef.current?.setMarkers([
        { id: 'chosen', latitude: value.latitude, longitude: value.longitude, selected: true },
      ]);
      controllerRef.current?.setView({ latitude: value.latitude, longitude: value.longitude });
    }
  }, [value]);

  if (unavailable) {
    return (
      <div className="gm-map gm-map--unavailable" style={{ minHeight: height }}>
        <EmptyState
          title="Map unavailable"
          description="Enter the latitude and longitude below instead."
        />
      </div>
    );
  }

  return (
    <div>
      <div ref={containerRef} className="gm-map" style={{ height }} aria-label="Choose a location" />
      <Text size="sm" muted>
        Tap the map to place the venue.
      </Text>
    </div>
  );
}
